"use client";

import { c as _c } from "react/compiler-runtime";
import { Itinerary } from "../components/itinerary";
import { useMemo } from "react";
import { AuthSurface } from "@/components/auth";
import { MfaDialog } from "@/components/mfa";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/shadcn/ui/drawer";
import { Button } from "@/components/ui";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/contexts/is-mobile-provider";
import { useSelfScheduleContext } from "@/contexts/self-schedule-provider";
import { usePatientClaim } from "@/hooks/patient-claim/use-patient-claim";
import { usePatientVerification } from "@/hooks/self-schedule/use-patient-verification";
import { cx } from "@/lib/tailwind-utils";
import { SelfScheduleStep } from "@/stores/self-schedule-store";

// Matches the API's limit on a self-scheduled appointment's note.
const PATIENT_NOTE_MAX_LENGTH = 1000;
interface ConfirmationProps {
  className?: string;
  itineraryClassName?: string;
  showItineraryTitle?: boolean;
  onCancel?: () => void;
  onConfirm?: () => void;
  useCategorySidebar?: boolean;
}
const Confirmation = t0 => {
  const $ = _c(32);
  const {
    className,
    itineraryClassName,
    showItineraryTitle: t1,
    onCancel,
    onConfirm,
    useCategorySidebar: t2
  } = t0;
  const showItineraryTitle = t1 === undefined ? true : t1;
  const useCategorySidebar = t2 === undefined ? false : t2;
  const {
    selectedServices,
    bookingLocation,
    selectedDateTime,
    setConfirmed,
    setPatientType,
    currentStep,
    firstStep,
    hasData,
    previousStep,
    patientNotesEnabled,
    patientNote,
    setPatientNote
  } = useSelfScheduleContext();
  const {
    claimedPatient: patient
  } = usePatientClaim();
  const {
    isSigningIn,
    setIsSigningIn
  } = usePatientVerification();
  const {
    isMobile
  } = useIsMobile();
  let t3;
  if ($[0] !== onConfirm || $[1] !== patient?.id || $[2] !== setConfirmed || $[3] !== setIsSigningIn) {
    t3 = () => {
      if (patient?.id) {
        setConfirmed(true);
        return;
      }
      if (onConfirm) {
        onConfirm();
      } else {
        setIsSigningIn(true);
      }
    };
    $[0] = onConfirm;
    $[1] = patient?.id;
    $[2] = setConfirmed;
    $[3] = setIsSigningIn;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleConfirm = t3;
  const completedAllData = !!selectedServices?.length && !!bookingLocation && !!selectedDateTime;
  const t4 = hasData && "md:w-64";
  const t5 = (currentStep === SelfScheduleStep.BOOKING_COMPLETE || currentStep === firstStep && isMobile) && "w-0 opacity-0 md:w-0 md:opacity-0";
  const t6 = currentStep === SelfScheduleStep.CONFIRMATION ? useCategorySidebar ? "md:w-full md:max-w-5xl" : "md:w-full md:max-w-3xl" : "sticky";
  let t7;
  if ($[5] !== className || $[6] !== t4 || $[7] !== t5 || $[8] !== t6) {
    t7 = cx("flex-shrink-0 overflow-hidden motion-ease-spring-bouncy top-[4.75rem]", "motion-scale-in-105 motion-opacity-in-85", t4, t5, t6, className);
    $[5] = className;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== completedAllData || $[11] !== currentStep || $[12] !== handleConfirm || $[13] !== hasData || $[14] !== itineraryClassName || $[15] !== onCancel || $[16] !== patientNote || $[17] !== patientNotesEnabled || $[18] !== previousStep || $[19] !== setPatientNote || $[20] !== showItineraryTitle) {
    t8 = currentStep === SelfScheduleStep.CONFIRMATION && !completedAllData ? <div className="flex h-64 items-center justify-center"><p className="text-muted-foreground">Missing required information</p></div> : <>{currentStep === SelfScheduleStep.CONFIRMATION && <div className="my-2"><h1 className="mb-2 hidden text-h1 md:block">Double Check</h1><p className="text-body md:text-muted-foreground text-primary-foreground">Please review your selections before confirming</p></div>}<Itinerary className={cx("w-full", hasData ? "max-w-full px-6 py-4 opacity-100 md:shadow-md transition-all duration-400" : "w-0 max-w-0 overflow-hidden p-0", hasData && (currentStep !== SelfScheduleStep.CONFIRMATION ? "bg-primary" : "md:border"), itineraryClassName)} showItineraryTitle={showItineraryTitle} />{currentStep === SelfScheduleStep.CONFIRMATION && patientNotesEnabled && <div className="mt-4 flex flex-col gap-1"><label htmlFor="self-schedule-patient-note" className="text-body-strong text-primary-foreground md:text-foreground">Note for your provider (optional)</label><Textarea id="self-schedule-patient-note" value={patientNote} onChange={e => setPatientNote(e.target.value)} maxLength={PATIENT_NOTE_MAX_LENGTH} rows={3} placeholder="Anything you'd like your provider to know before your visit" /></div>}{currentStep === SelfScheduleStep.CONFIRMATION && <div className="flex gap-2 sticky bottom-0 md:relative"><Button variant="secondary" className="mt-4 w-full md:hidden" onClick={() => {
          previousStep();
          onCancel?.();
        }}><span>Make Changes</span></Button><Button variant="primary" className="mt-4 w-full" onClick={() => handleConfirm()}><span>Confirm</span></Button></div>}</>;
    $[10] = completedAllData;
    $[11] = currentStep;
    $[12] = handleConfirm;
    $[13] = hasData;
    $[14] = itineraryClassName;
    $[15] = onCancel;
    $[16] = patientNote;
    $[17] = patientNotesEnabled;
    $[18] = previousStep;
    $[19] = setPatientNote;
    $[20] = showItineraryTitle;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== isSigningIn || $[23] !== onConfirm || $[24] !== setConfirmed || $[25] !== setIsSigningIn || $[26] !== setPatientType) {
    t9 = !onConfirm && <MfaDialog open={isSigningIn} onOpenChange={setIsSigningIn} surface={AuthSurface.SELF_SCHEDULE} onPatientVerified={verifiedPatient => {
      setPatientType(verifiedPatient.patientStatus);
      setConfirmed(true);
    }} description="Enter your phone number to confirm your appointment." tAndSFooterText="confirming your appointment" />;
    $[22] = isSigningIn;
    $[23] = onConfirm;
    $[24] = setConfirmed;
    $[25] = setIsSigningIn;
    $[26] = setPatientType;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t7 || $[29] !== t8 || $[30] !== t9) {
    t10 = <div data-testid="step-confirmation" className={t7}>{t8}{t9}</div>;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
};
export default Confirmation;
export const ConfirmationDrawer = t0 => {
  const $ = _c(30);
  const {
    isOpen,
    setIsOpen,
    className
  } = t0;
  const {
    currentStep,
    setPatientType
  } = useSelfScheduleContext();
  const {
    isSigningIn,
    setIsSigningIn
  } = usePatientVerification();
  let t1;
  if ($[0] !== setIsOpen) {
    t1 = () => {
      setIsOpen(false);
    };
    $[0] = setIsOpen;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const onCancel = t1;
  let t2;
  if ($[2] !== setIsOpen || $[3] !== setIsSigningIn) {
    t2 = () => {
      setIsOpen(false);
      setIsSigningIn(true);
    };
    $[2] = setIsOpen;
    $[3] = setIsSigningIn;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const onConfirm = t2;
  const t3 = currentStep === SelfScheduleStep.CONFIRMATION && "pb-12";
  let t4;
  if ($[5] !== className || $[6] !== t3) {
    t4 = cx("max-h-[90vh] bg-primary/90 backdrop-blur-sm", t3, className);
    $[5] = className;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = currentStep === SelfScheduleStep.CONFIRMATION ? "Confirm Your Appointment" : "Your Selections";
  let t6;
  if ($[8] !== t5) {
    t6 = <DrawerHeader><DrawerTitle><span className="text-primary-foreground">{t5}</span></DrawerTitle></DrawerHeader>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== onCancel || $[11] !== onConfirm) {
    t7 = <div className="overflow-y-auto px-4 pb-4"><Confirmation className="p-0" itineraryClassName="bg-transparent p-0" showItineraryTitle={false} onCancel={onCancel} onConfirm={onConfirm} /></div>;
    $[10] = onCancel;
    $[11] = onConfirm;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t4 || $[14] !== t6 || $[15] !== t7) {
    t8 = <DrawerContent className={t4}>{t6}{t7}</DrawerContent>;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== isOpen || $[18] !== setIsOpen || $[19] !== t8) {
    t9 = <Drawer open={isOpen} onOpenChange={setIsOpen}>{t8}</Drawer>;
    $[17] = isOpen;
    $[18] = setIsOpen;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== setPatientType) {
    t10 = verifiedPatient => {
      setPatientType(verifiedPatient.patientStatus);
    };
    $[21] = setPatientType;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== isSigningIn || $[24] !== setIsSigningIn || $[25] !== t10) {
    t11 = <MfaDialog open={isSigningIn} onOpenChange={setIsSigningIn} surface={AuthSurface.SELF_SCHEDULE} onPatientVerified={t10} description="Enter your phone number to confirm your appointment." tAndSFooterText="confirming your appointment" />;
    $[23] = isSigningIn;
    $[24] = setIsSigningIn;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11 || $[28] !== t9) {
    t12 = <>{t9}{t11}</>;
    $[27] = t11;
    $[28] = t9;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
};